import * as PropTypes from "prop-types";
import classNames from "classnames";
import Waypoint from "react-waypoint";
import isEqual from "react-fast-compare";

import videoStyles from "./Video.css";
import { isIeOrEdge, isMobile } from "/javascripts/checks";

import { withViewport } from "/react/utils/withViewport";

import {
  AudioAnimation,
  ButtonWithIcon,
  ButtonWithText,
  IconPlay,
  Photo,
} from "/react/library/";

@withViewport()
export class Video extends Component {
  static propTypes = {
    fillsParent: PropTypes.bool,
    fitsInParent: PropTypes.bool,
    hasAudioToggle: PropTypes.bool,
    hasAutoplay: PropTypes.bool,
    hasControls: PropTypes.bool,
    hasOverlay: PropTypes.bool,
    isLooped: PropTypes.bool,
    onLoad: PropTypes.func,
    onVideoEnd: PropTypes.func,
    onVideoPlay: PropTypes.func,
    src: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        960: PropTypes.string.isRequired,
        1280: PropTypes.string.isRequired,
        1920: PropTypes.string.isRequired,
      }),
    ]).isRequired,
    startTime: PropTypes.number,
    startsWithSound: PropTypes.bool,
    thumbnail: PropTypes.string,
  };

  static defaultProps = {
    startTime: 0,
    startsWithSound: false,
  };

  state = {
    hasSound: this.props.startsWithSound,
    hasActiveThumbnail: !!this.props.thumbnail,
    isMounted: false,
    isPlaying: false,
  };

  inViewportMargin = "-1%";
  isInViewport = false;
  isManualPlaybackEnabled = false;
  videoRef = React.createRef();

  /*
  This will go through the available video's per size, sort them and return the first video that is bigger (or equal)
  than the viewport. This effectively will only load the smallest video needed, saving bandwidth on mobile devices.
   */
  getResponsiveSource = ({ viewportWidth, src }) => {
    const videoSize = Object.keys(src)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      .find((videoWidth) => parseInt(videoWidth, 10) >= viewportWidth);

    return src[videoSize];
  };

  hasVideoRef = () => !!this.videoRef.current;

  isAllowedToPlay = () =>
    this.props.hasAutoplay ? this.isInViewport : this.isManualPlaybackEnabled;

  playVideo = () => {
    if (!this.hasVideoRef()) return;

    const video = this.videoRef.current;
    const currentTime = this.props.startTime;

    if (currentTime) video.currentTime = currentTime;

    video.play();
  };

  pauseVideo = () => {
    if (!this.hasVideoRef()) return;

    this.videoRef.current.pause();
  };

  toggleAudioClick = () => {
    if (!this.hasVideoRef()) return;

    this.videoRef.current.muted = !this.videoRef.current.muted;
  };

  handleVideoInsideViewportEvent = () => {
    this.isInViewport = true;
    if (this.isAllowedToPlay()) this.playVideo();
  };

  handleVideoOutsideViewportEvent = () => {
    this.isInViewport = false;
    this.pauseVideo();
  };

  handleThumbnailClick = () => {
    this.setState(
      { hasActiveThumbnail: false },
      this.handleVideoPlaybackToggleClick
    );
  };

  handleVideoPlaybackToggleClick = () => {
    this.isManualPlaybackEnabled = !this.isManualPlaybackEnabled;

    if (this.hasVideoRef()) {
      this.videoRef.current.paused ? this.playVideo() : this.pauseVideo();
    }
  };

  handleVideoLoadEvent = () => {
    const videoLoadCallback = this.props.onLoad;

    videoLoadCallback && videoLoadCallback();
  };

  handleVideoPlayEvent = () => {
    const videoPlayCallback = this.props.onVideoPlay;

    // On mobile a video will not load if it doesn't have the autoplay property. So we add autoplay for all mobile
    // devices to trigger loading, and playing. But sometimes we want to start the video manually, so we pause the video
    // immediately when it starts playing but should't autoplay based on it's props.
    if (isMobile && !this.isAllowedToPlay()) {
      this.pauseVideo();
      return;
    }

    videoPlayCallback && videoPlayCallback();
    this.setState({ isPlaying: true });
  };

  handleVideoPauseEvent = () => {
    this.setState({ isPlaying: false });
  };

  handleVideoEndedEvent = () => {
    const videoEndCallback = this.props.onVideoEnd;

    videoEndCallback && videoEndCallback();
    this.setState({ isPlaying: false });
  };

  handleVideoVolumeChangedEvent = () => {
    this.setState({ hasSound: !this.videoRef.current.muted });
  };

  shouldComponentUpdate(nextProps, nextState) {
    return !(isEqual(this.props, nextProps) && isEqual(this.state, nextState));
  }

  componentDidMount() {
    this.setState({ isMounted: true });
  }

  render() {
    const {
      hasControls,
      fillsParent,
      fitsInParent,
      hasAudioToggle,
      hasOverlay,
      isLooped,
      src,
      startsWithSound,
      thumbnail,
      viewportWidth,
    } = this.props;

    const { hasActiveThumbnail, hasSound, isMounted, isPlaying } = this.state;

    if (!isMounted) return null;

    const source =
      typeof src === "object"
        ? this.getResponsiveSource({ viewportWidth, src })
        : src;

    return (
      <Waypoint
        onEnter={this.handleVideoInsideViewportEvent}
        onLeave={this.handleVideoOutsideViewportEvent}
        topOffset={this.inViewportMargin}
        bottomOffset={this.inViewportMargin}
      >
        <div
          className={classNames(videoStyles.video, {
            [videoStyles.videoFillsParent]: fillsParent,
            [videoStyles.videoFitsInParent]: fitsInParent,
          })}
        >
          <div
            className={classNames({
              [videoStyles.videoHasOverlay]: hasOverlay && !hasControls,
            })}
          >
            {thumbnail && (
              <div
                className={classNames(videoStyles.thumbnail, {
                  [videoStyles.thumbnailIsHidden]: !hasActiveThumbnail,
                })}
                onClick={this.handleThumbnailClick}
              >
                <Photo src={thumbnail} fillsParent />
                <div className={videoStyles.thumbnailPlayButton}>
                  {/* <ButtonWithIcon isLight>
                    <IconPlay isLight />
                  </ButtonWithIcon> */}
                  <ButtonWithText hasFillVideo id="video-play-btn">
                    Watch Video
                  </ButtonWithText>
                </div>
              </div>
            )}

            {hasAudioToggle && !hasControls && (
              <div
                className={videoStyles.controlsContainer}
                onClick={this.toggleAudioClick}
              >
                <AudioAnimation isPlaying={isPlaying && hasSound} />
              </div>
            )}

            {!hasActiveThumbnail && (
              <video
                autoPlay={isMobile}
                className={classNames(videoStyles.player, {
                  [videoStyles.playerFillsContainer]:
                    fillsParent && !isIeOrEdge,
                  [videoStyles.playerFillsContainerForIeAndEdge]:
                    fillsParent && isIeOrEdge,
                })}
                controls={hasControls}
                // controlsList="nodownload nofullscreen"
                controlsList="nodownload"
                loop={isLooped}
                muted={!startsWithSound}
                onEnded={this.handleVideoEndedEvent}
                onLoadedData={this.handleVideoLoadEvent}
                onPause={this.handleVideoPauseEvent}
                onPlay={this.handleVideoPlayEvent}
                onVolumeChange={this.handleVideoVolumeChangedEvent}
                playsInline
                ref={this.videoRef}
              >
                <source src={source} type="video/mp4" />
              </video>
            )}
          </div>
        </div>
      </Waypoint>
    );
  }
}
